import axios from "axios";
import { getJwtToken } from "./jwt.services";

// interns scriptrunner - Use REACT_APP_BASE_URL or fallback to localhost:5001 as per V1 pattern
const BASE_URL =
  process.env.REACT_APP_BASE_URL ||
  (window.location.origin.includes("localhost") ? "http://localhost:5001" : "");

// interns scriptrunner - Standardized prefix to avoid path collisions
const API_PREFIX = "/script-runner-v2";

// interns scriptrunner - Helper to ensure clean URLs
const getUrl = (path) =>
  `${BASE_URL.replace(/\/$/, "")}${API_PREFIX}${path.startsWith("/") ? path : "/" + path}`;

// interns scriptrunner - Helper to create headers matching V1's working pattern
const getHeaders = (isMultipart = false) => {
  const jwtToken = getJwtToken();
  const headers = {
    Authorization: `Bearer ${jwtToken}`,
    "Content-Type": isMultipart ? "multipart/form-data" : "application/json",
  };
  return headers;
};

// interns scriptrunner - Service for Script Runner V2 API calls
const scriptRunnerV2Service = {
  // interns scriptrunner - Fetch all scripts metadata
  getAllScripts: async () => {
    const headers = getHeaders();
    const url = getUrl("/scripts");

    console.log("interns scriptrunner - getAllScripts Request Details:", {
      url,
    });

    const response = await axios.get(url, { headers });
    return response.data.data;
  },

  // interns scriptrunner - Create a new script entry
  createScript: async (scriptData) => {
    const headers = getHeaders();
    const url = getUrl("/scripts");

    const response = await axios.post(url, scriptData, { headers });
    return response.data.data;
  },

  // interns scriptrunner - Upload a new script version
  uploadVersion: async (scriptId, formData) => {
    const headers = getHeaders(true);
    const url = getUrl(`/scripts/${scriptId}/versions`);

    const response = await axios.post(url, formData, { headers });
    return response.data.data;
  },

  // interns scriptrunner - Fetch a single script's metadata
  getScriptById: async (scriptId) => {
    const headers = getHeaders();
    const url = getUrl(`/scripts/${scriptId}`);

    const response = await axios.get(url, { headers });
    return response.data.data;
  },

  // interns scriptrunner - Download ZIP content as an arraybuffer for jszip
  downloadZip: async (scriptId, version) => {
    const headers = getHeaders();
    const url = getUrl(`/scripts/${scriptId}/versions/${version}/download`);

    const response = await axios.get(url, {
      headers,
      responseType: "arraybuffer",
    });
    return response.data;
  },

  // interns scriptrunner - Fetch all versions for a specific script
  getScriptVersions: async (scriptId) => {
    const headers = getHeaders();
    const url = getUrl(`/scripts/${scriptId}/versions`);

    const response = await axios.get(url, { headers });
    return response.data.data;
  },

  // interns scriptrunner - Execute a script version with provided inputs
  executeScript: async (scriptId, version, inputs) => {
    // interns scriptrunner - Check if any input is a File to decide on multipart/form-data
    const hasFiles = Object.values(inputs).some((val) => val instanceof File);
    const headers = getHeaders(hasFiles);
    const url = getUrl(`/scripts/${scriptId}/versions/${version}/execute`);

    let payload;
    if (hasFiles) {
      payload = new FormData();
      // interns scriptrunner - We wrap inputs in a stringified JSON field just like V1 sometimes does,
      // or we can send them as individual fields. Let's send them as individual fields + a special 'inputs' field for consistency.
      const plainInputs = {};
      Object.entries(inputs).forEach(([key, value]) => {
        if (value instanceof File) {
          payload.append(key, value);
        } else {
          plainInputs[key] = value;
        }
      });
      payload.append("inputs", JSON.stringify(plainInputs));
    } else {
      payload = { inputs };
    }

    console.log(
      "interns scriptrunner - [EXECUTION START] Clicking Run button",
      { hasFiles },
    );

    const response = await axios.post(url, payload, { headers });
    return response.data.data;
  },

  // interns scriptrunner - Fetch execution history
  getExecutions: async (params = {}) => {
    const headers = getHeaders();
    const url = getUrl("/executions");
    const response = await axios.get(url, { headers, params });
    return response.data.data;
  },

  // interns scriptrunner - Fetch single execution status
  getExecutionById: async (executionId) => {
    const headers = getHeaders();
    const url = getUrl(`/executions/${executionId}`);
    const response = await axios.get(url, { headers });

    if (
      ["completed", "failed", "terminated"].includes(response.data.data.status)
    ) {
      console.log(
        "interns scriptrunner - [STATUS UPDATE] Final Status:",
        response.data.data.status,
      );
      console.log(
        "interns scriptrunner - [STATUS UPDATE] Output metadata:",
        response.data.data.output,
      );
    }
    return response.data.data;
  },

  // ============================================================
  // gsd-py-runner added this function - getExecutionLogs - fetch logs only : Start
  // ============================================================
  getExecutionLogs: async (executionId) => {
    const headers = getHeaders();
    const url = getUrl(`/executions/${executionId}/logs`);
    const response = await axios.get(url, { headers });
    return response.data; // { status: "ok", logs: [...] }
  },
  // ============================================================
  // gsd-py-runner added this function - getExecutionLogs - fetch logs only : End
  // ============================================================

  // interns scriptrunner - Download execution files as ZIP
  downloadExecutionFiles: async (executionId) => {
    const headers = getHeaders();
    const url = getUrl(`/executions/${executionId}/download`);

    // interns scriptrunner - triggering download tracing
    console.log(
      "interns scriptrunner - [DOWNLOAD] Triggering download trace for:",
      executionId,
    );
    console.log("interns scriptrunner - [DOWNLOAD] API Endpoint:", url);

    const response = await axios.get(url, {
      headers,
      responseType: "blob",
    });

    console.log(
      "interns scriptrunner - [DOWNLOAD] Response Status:",
      response.status,
    );
    return response.data;
  },

  // interns scriptrunner - Pause execution
  pauseExecution: async (executionId) => {
    const headers = getHeaders();
    const url = getUrl(`/executions/${executionId}/pause`);
    const response = await axios.post(url, {}, { headers });
    return response.data;
  },

  // interns scriptrunner - Resume execution
  resumeExecution: async (executionId) => {
    const headers = getHeaders();
    const url = getUrl(`/executions/${executionId}/resume`);
    const response = await axios.post(url, {}, { headers });
    return response.data;
  },

  // interns scriptrunner - Terminate execution
  terminateExecution: async (executionId) => {
    const headers = getHeaders();
    const url = getUrl(`/executions/${executionId}/terminate`);
    const response = await axios.post(url, {}, { headers });
    return response.data;
  },

  // interns scriptrunner - Delete a script (soft delete)
  deleteScript: async (scriptId) => {
    const headers = getHeaders();
    const url = getUrl(`/scripts/${scriptId}`);
    const response = await axios.delete(url, { headers });
    return response.data;
  },

  // interns scriptrunner - Delete a script version (soft delete)
  deleteScriptVersion: async (scriptId, versionId) => {
    const headers = getHeaders();
    const url = getUrl(`/scripts/${scriptId}/versions/${versionId}`);
    const response = await axios.delete(url, { headers });
    return response.data;
  },

  // interns scriptrunner - List all files available for a specific execution
  listExecutionFiles: async (executionId) => {
    const headers = getHeaders();
    const url = getUrl(`/executions/${executionId}/list-files`);
    const response = await axios.get(url, { headers });
    return response.data.data;
  },

  // interns scriptrunner - Download a specific artifact
  downloadSingleFile: async (executionId, fileKey) => {
    const headers = getHeaders();
    const url = getUrl(`/executions/${executionId}/download-file`);
    const response = await axios.get(url, {
      headers,
      params: { fileKey },
      responseType: "blob",
    });
    return response.data;
  },

  // interns scriptrunner - Delete an execution (soft delete)
  deleteExecution: async (executionId) => {
    const headers = getHeaders();
    const url = getUrl(`/executions/${executionId}`);
    const response = await axios.delete(url, { headers });
    return response.data;
  },
};

export default scriptRunnerV2Service;
